import { useRef, useState } from 'react';
import Joyride, { CallBackProps, STATUS, Step, Styles } from 'react-joyride';
import { useLocalStorage } from '@/hooks/use-local-storage';

interface AppTourProps {
    steps: Step[];
    run: boolean;
    tourKey: string; // Unique key to track if user has seen this specific tour
    onComplete?: () => void;
    /**
     * Replays the tour for someone who already finished it. Remount with a new
     * `key` to run it again — Joyride only starts on a false → true `run`.
     */
    replay?: boolean;
}

export function AppTour({ steps, run, tourKey, onComplete, replay = false }: AppTourProps) {
    const [hasSeenTour, setHasSeenTour] = useLocalStorage<boolean>(`tour_seen_${tourKey}`, false);
    const [hasFinished, setHasFinished] = useState(false);
    const hasCompletedCallbackRef = useRef(false);

    const shouldRun = !hasFinished && (replay || (run && !hasSeenTour));

    const handleCallback = (data: CallBackProps) => {
        const { status } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setHasFinished(true);
            setHasSeenTour(true);

            if (!hasCompletedCallbackRef.current) {
                hasCompletedCallbackRef.current = true;
                onComplete?.();
            }
        }
    };

    if (hasSeenTour && !replay) return null;

    const styles: Partial<Styles> = {
        options: {
            primaryColor: '#01006b', // Brand Blue
            textColor: '#1e293b', // Slate 800
            zIndex: 1000,
        },
        tooltip: {
            borderRadius: '0.5rem',
            fontFamily: 'inherit',
        },
        buttonNext: {
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 500,
        },
        buttonBack: {
            marginRight: '10px',
            fontSize: '14px',
            color: '#64748b',
        },
    };

    return (
        <Joyride
            steps={steps}
            run={shouldRun}
            continuous
            showProgress
            showSkipButton
            styles={styles}
            callback={handleCallback}
            disableOverlayClose={true}
            locale={{
                back: 'Voltar',
                close: 'Fechar',
                last: 'Concluir',
                next: 'Próximo',
                // With showProgress, Joyride reads this key instead of `next`.
                nextLabelWithProgress: 'Próximo (etapa {step} de {steps})',
                open: 'Abrir',
                skip: 'Pular',
            }}
            floaterProps={{
                disableAnimation: true,
            }}
        />
    );
}
